import { useState, useRef, ChangeEvent, DragEvent, useEffect } from 'react';

// Utils
import { showErrorToast } from '@/utils/errorHandlers';

// Constants
import {
  MAX_FILE_SIZE,
  MAX_IMAGE_DIMENSION,
  UPLOAD_FILE_MESSAGE,
  IMAGES,
} from '@shared/constants';

export interface FileFormat {
  mimeType: string;
  extensions: string[];
  label?: string;
}

interface UseFileUploadOptions {
  maxSize?: number;
  allowedFormats?: FileFormat[];
  onFileSelect?: (file: File | null) => void;
  defaultFileUrl?: string;
}

interface UseFileUploadReturn {
  selectedFile: File | null;
  previewUrl: string | null;
  isDragging: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  dropZoneRef: React.RefObject<HTMLDivElement | null>;
  handleFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleDragOver: (e: DragEvent<HTMLDivElement>) => void;
  handleDragLeave: (e: DragEvent<HTMLDivElement>) => void;
  handleDrop: (e: DragEvent<HTMLDivElement>) => void;
  handleSelectFile: () => void;
  handleRemoveFile: () => void;
  acceptString: string;
}

export const useFileUpload = ({
  maxSize = MAX_FILE_SIZE,
  allowedFormats = [],
  onFileSelect,
  defaultFileUrl,
}: UseFileUploadOptions = {}): UseFileUploadReturn => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);

  // Extract MIME types and extensions
  const allowedMimeTypes = allowedFormats.map((format) => format.mimeType);
  const allowedExtensions = allowedFormats.flatMap(
    (format) => format.extensions,
  );

  // Function to validate file type
  const validateFileType = (file: File): boolean => {
    if (allowedFormats.length === 0) return true;

    const isValidMimeType = allowedMimeTypes.includes(file.type);
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const isValidExtension = allowedExtensions.includes(fileExtension);

    return isValidMimeType && isValidExtension;
  };

  const { EXCEED_FILE_SIZE, INVALID_FILE_TYPE, EXCEED_IMAGE_DIMENSION } =
    UPLOAD_FILE_MESSAGE;

  // Get readable format list for error messages
  const getReadableFormats = (): string => {
    return allowedFormats
      .map(
        (format) =>
          format.label ||
          format.extensions
            .map((ext) => ext.replace('.', '').toUpperCase())
            .join('/'),
      )
      .join(', ');
  };

  const processFile = (file: File) => {
    // Check file type
    if (!validateFileType(file)) {
      showErrorToast(INVALID_FILE_TYPE(getReadableFormats()));
      return;
    }

    // Check file size
    const fileSize = file.size / 1024;
    if (fileSize > maxSize) {
      showErrorToast(EXCEED_FILE_SIZE);
      return;
    }

    // Check file dimensions
    const image = new Image();
    image.src = URL.createObjectURL(file);

    image.onload = () => {
      const { width, height } = image;

      if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
        showErrorToast(EXCEED_IMAGE_DIMENSION);
        URL.revokeObjectURL(image.src);
        return;
      }

      // Create preview URL
      const objectUrl = URL.createObjectURL(file);

      setPreviewUrl(objectUrl);
      setSelectedFile(file);
      onFileSelect?.(file);

      URL.revokeObjectURL(image.src);
    };

    image.onerror = () => {
      showErrorToast(UPLOAD_FILE_MESSAGE.CORRUPTED_IMAGE);
      URL.revokeObjectURL(image.src);
    };
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    e.target.value = '';

    if (!file) return;

    processFile(file);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];

    if (!file) return;

    processFile(file);
  };

  const handleSelectFile = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveFile = () => {
    if (previewUrl && !previewUrl.startsWith('data:')) {
      // Only revoke if it's an object URL, not a base64 string
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(null);
    setPreviewUrl(null);
    onFileSelect?.(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const acceptString = allowedExtensions.join(',');

  // Initialize with defaultFileUrl if provided
  useEffect(() => {
    // If there is no defaultFileUrl, remove the preview
    if (!defaultFileUrl) {
      if (previewUrl && !previewUrl.startsWith('data:')) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(null);
      setSelectedFile(null);
      return;
    }

    if (selectedFile || defaultFileUrl === previewUrl) {
      return;
    }

    const isDefaultAvatar =
      defaultFileUrl === IMAGES.AVATAR_DEFAULT ||
      defaultFileUrl === IMAGES.AVATAR_DEFAULT_PNG;

    if (isDefaultAvatar && selectedFile) {
      return;
    }

    // Clean up old URL if needed
    if (previewUrl && !previewUrl.startsWith('data:')) {
      URL.revokeObjectURL(previewUrl);
    }

    // Update previewUrl
    setPreviewUrl(defaultFileUrl);
  }, [defaultFileUrl, previewUrl, selectedFile]);

  return {
    selectedFile,
    previewUrl,
    isDragging,
    fileInputRef,
    dropZoneRef,
    handleFileChange,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleSelectFile,
    handleRemoveFile,
    acceptString,
  };
};
